import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { BotRegistrationDialog } from "@/components/BotRegistrationDialog";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  ChevronLeft, Sparkles, Send, Plus, Trash2, MoreVertical, Copy, Download, RefreshCw, Search, AlertCircle, ExternalLink,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";

const SPEAKER_PALETTE = [
  "hsl(201 96% 32%)", "hsl(160 84% 24%)", "hsl(18 91% 40%)",
  "hsl(270 50% 45%)", "hsl(38 92% 36%)",
];

const PRIORITY_COLORS = {
  high: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400",
  medium: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400",
  low: "bg-stone-200 text-stone-700 dark:bg-stone-800 dark:text-stone-300",
};

export default function MeetingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/meetings/${id}`);
      setData(res.data);
      if (res.data.meeting.status === "processing" || res.data.meeting.status === "recording") {
        setPolling(true);
      } else {
        setPolling(false);
      }
    } catch (e) {
      console.error("Failed to load meeting", e);
      toast.error("Failed to load meeting");
      navigate("/app/meetings");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!polling) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [polling, load]);

  if (loading) {
    return (
      <AppShell>
        <div className="mx-auto max-w-6xl px-6 py-10 md:px-10 space-y-4">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-12 rounded-xl" />
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </AppShell>
    );
  }

  const { meeting, transcript, summary, action_items } = data;
  const dt = new Date(meeting.scheduled_at);

  const handleDelete = async () => {
    await api.delete(`/meetings/${id}`);
    toast.success("Meeting deleted");
    navigate("/app/meetings");
  };

  const handleRegenerate = async () => {
    try {
      await api.post(`/meetings/${id}/regenerate`);
      toast.success("Regenerating summary…");
      setPolling(true);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to regenerate");
    }
  };

  const handleSync = async () => {
    try {
      const res = await api.post(`/meetings/${id}/sync`);
      const recallStatus = res.data.recall_status;
      if (res.data.transcript_fetched) {
        toast.success("Transcript fetched — Noteology is analyzing now");
      } else {
        toast.info(`Recall status: ${recallStatus}. Continuing to poll…`);
      }
      setPolling(true);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    }
  };

  const handleCopyMarkdown = () => {
    const md = buildMarkdown(meeting, summary, action_items);
    navigator.clipboard.writeText(md);
    toast.success("Copied to clipboard");
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
        <Link to="/app/meetings" className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-library">
          <ChevronLeft className="h-4 w-4" /> All meetings
        </Link>

        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {dt.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
              {" · "}{Math.round((meeting.duration_seconds || 0) / 60)} MIN
              {" · "}<span className="uppercase">{meeting.platform}</span>
            </div>
            <h1 className="mt-2 font-display text-4xl font-bold tracking-tight">{meeting.title}</h1>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Badge variant="outline" className="capitalize" data-testid="meeting-status">
                {meeting.status === "processing" ? <Sparkles className="mr-1 h-3 w-3 animate-pulse" /> : null}
                {meeting.status}
              </Badge>
              <div className="flex -space-x-2">
                {(meeting.attendees || []).map((a, i) => (
                  <InitialsAvatar key={i} name={a.name} size="sm" className="ring-2 ring-background" />
                ))}
              </div>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="meeting-actions-menu">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleRegenerate} data-testid="action-regenerate">
                <RefreshCw className="mr-2 h-4 w-4" /> Regenerate summary
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCopyMarkdown} data-testid="action-copy-markdown">
                <Copy className="mr-2 h-4 w-4" /> Copy as markdown
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDelete} className="text-destructive" data-testid="action-delete">
                <Trash2 className="mr-2 h-4 w-4" /> Delete meeting
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Processing/Recording banner */}
        {["processing", "recording", "scheduled"].includes(meeting.status) && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-950/30">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-sm text-amber-900 dark:text-amber-200">
                  <Sparkles className="h-4 w-4 animate-pulse" />
                  <span className="font-medium capitalize">{meeting.status}…</span>
                </div>
                <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-300/80">
                  {meeting.status === "processing"
                    ? "Noteology is analyzing this meeting. Usually takes 30–90 seconds."
                    : "The bot is in the meeting. We'll update this page automatically once it ends."}
                </p>
              </div>
              {meeting.recall_bot_id && !meeting.recall_bot_id.startsWith("mock_") && (
                <Button size="sm" variant="outline" onClick={handleSync} data-testid="sync-from-recall-button">
                  <RefreshCw className="mr-2 h-3 w-3" /> Sync from Recall.ai
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Calendar override banner */}
        {meeting.source === "calendar" && ["scheduled", "skipped"].includes(meeting.status) && (
          <CalendarOverrideBanner meeting={meeting} onChanged={load} />
        )}

        {/* Error banner */}
        {meeting.status === "failed" && (
          <FailedBanner
            meeting={meeting}
            onRetry={() => setRetryOpen(true)}
            onReload={load}
          />
        )}

        <BotRegistrationDialog
          open={retryOpen}
          onOpenChange={setRetryOpen}
          meetingType={meeting.meeting_type}
          errorCode={meeting.error_code}
          defaults={{
            bot_name: (meeting.registration_data?.bot_name) || user?.bot_default_name || "Noteology AI",
            bot_email: (meeting.registration_data?.bot_email) || user?.bot_default_email || "",
            bot_company: (meeting.registration_data?.bot_company) || user?.bot_default_company || "",
          }}
          submitting={retrying}
          onSubmit={async (vals) => {
            setRetrying(true);
            try {
              await api.post(`/meetings/${id}/retry`, vals);
              toast.success("Retrying bot deployment");
              setRetryOpen(false);
              setPolling(true);
              load();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Retry failed");
            } finally {
              setRetrying(false);
            }
          }}
        />

        {/* Tabs */}
        <Tabs defaultValue="summary" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-4">
            <TabsTrigger value="summary" data-testid="tab-summary">Summary</TabsTrigger>
            <TabsTrigger value="transcript" data-testid="tab-transcript">Transcript</TabsTrigger>
            <TabsTrigger value="chat" data-testid="tab-chat">Chat</TabsTrigger>
            <TabsTrigger value="insights" data-testid="tab-insights">Insights</TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="mt-6">
            <SummaryTab summary={summary} actions={action_items} meetingId={id} onReload={load} />
          </TabsContent>

          <TabsContent value="transcript" className="mt-6">
            <TranscriptTab transcript={transcript} />
          </TabsContent>

          <TabsContent value="chat" className="mt-6">
            <ChatTab meetingId={id} hasTranscript={!!transcript} attendees={meeting.attendees || []} />
          </TabsContent>

          <TabsContent value="insights" className="mt-6">
            <InsightsTab transcript={transcript} summary={summary} />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

function SummaryTab({ summary, actions, meetingId, onReload }) {
  const [newOwner, setNewOwner] = useState("");
  const [newTask, setNewTask] = useState("");

  if (!summary) {
    return (
      <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">
        Summary not yet available. Once processing completes it will appear here.
      </div>
    );
  }

  const toggle = async (a) => {
    const newStatus = a.status === "done" ? "pending" : "done";
    await api.patch(`/action-items/${a.action_id}`, { status: newStatus });
    onReload();
  };

  const remove = async (a) => {
    await api.delete(`/action-items/${a.action_id}`);
    toast.success("Action item removed");
    onReload();
  };

  const addAction = async (e) => {
    e.preventDefault();
    if (!newOwner.trim() || !newTask.trim()) return;
    await api.post("/action-items", {
      meeting_id: meetingId, owner: newOwner, task: newTask, priority: "medium",
    });
    setNewOwner(""); setNewTask("");
    onReload();
    toast.success("Action item added");
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-6">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Summary</div>
          <div className="prose prose-stone dark:prose-invert mt-3 max-w-none text-[15px] leading-relaxed whitespace-pre-wrap">
            {summary.summary}
          </div>
        </div>

        {summary.key_decisions?.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Key decisions</div>
            <ul className="mt-4 space-y-3">
              {summary.key_decisions.map((d, i) => (
                <li key={i} className="flex gap-3">
                  <span className="font-mono text-xs text-primary mt-1">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm">{d}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Action items</div>
            <span className="font-mono text-xs text-muted-foreground">{actions?.length || 0}</span>
          </div>

          <div className="space-y-3" data-testid="action-items-list">
            {(actions || []).map((a) => (
              <div key={a.action_id} className="group flex items-start gap-3 rounded-lg border border-border bg-background p-3" data-testid={`action-${a.action_id}`}>
                <Checkbox
                  checked={a.status === "done"}
                  onCheckedChange={() => toggle(a)}
                  data-testid={`action-checkbox-${a.action_id}`}
                />
                <div className="min-w-0 flex-1">
                  <div className={`text-sm ${a.status === "done" ? "line-through text-muted-foreground" : ""}`}>{a.task}</div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                    <InitialsAvatar name={a.owner} size="xs" />
                    <span className="text-muted-foreground">{a.owner}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${PRIORITY_COLORS[a.priority] || ""}`}>
                      {a.priority}
                    </span>
                    {a.due_date && <span className="font-mono text-muted-foreground">DUE {a.due_date}</span>}
                  </div>
                </div>
                <button onClick={() => remove(a)} className="opacity-0 group-hover:opacity-100" data-testid={`delete-action-${a.action_id}`}>
                  <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                </button>
              </div>
            ))}
            {(!actions || actions.length === 0) && (
              <div className="text-center text-xs text-muted-foreground py-4">No action items yet.</div>
            )}
          </div>

          <form onSubmit={addAction} className="mt-4 space-y-2 border-t border-border pt-4">
            <Input placeholder="Owner name" value={newOwner} onChange={(e) => setNewOwner(e.target.value)} data-testid="new-action-owner" />
            <Input placeholder="What needs to happen?" value={newTask} onChange={(e) => setNewTask(e.target.value)} data-testid="new-action-task" />
            <Button type="submit" size="sm" variant="outline" className="w-full" data-testid="add-action-button">
              <Plus className="mr-1 h-3 w-3" /> Add action item
            </Button>
          </form>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Sentiment</div>
          <div className="mt-3 flex items-center gap-3">
            <SentimentDot value={summary.sentiment} />
            <span className="text-sm font-medium capitalize">{summary.sentiment}</span>
          </div>
          {summary.meeting_type && (
            <div className="mt-4 font-mono text-xs text-muted-foreground">
              TYPE · <span className="uppercase">{summary.meeting_type}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SentimentDot({ value }) {
  const map = { positive: "bg-emerald-500", neutral: "bg-stone-400", tense: "bg-rose-500" };
  return <span className={`h-3 w-3 rounded-full ${map[value] || "bg-stone-400"}`} />;
}

function TranscriptTab({ transcript }) {
  const [q, setQ] = useState("");

  if (!transcript) {
    return (
      <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">
        Transcript not yet available.
      </div>
    );
  }

  const speakers = transcript.speakers || [];
  const speakerColor = (name) => {
    const idx = speakers.findIndex((s) => s.name === name);
    return SPEAKER_PALETTE[Math.max(0, idx) % SPEAKER_PALETTE.length];
  };

  const filtered = q
    ? (transcript.segments || []).filter((s) => s.text.toLowerCase().includes(q.toLowerCase()))
    : (transcript.segments || []);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr,260px]">
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search transcript…"
            className="pl-9"
            data-testid="transcript-search"
          />
        </div>
        <div className="space-y-5 max-h-[60vh] overflow-y-auto" data-testid="transcript-content">
          {filtered.map((seg, i) => (
            <div key={`${seg.start_time}-${seg.speaker}-${i}`} className="flex gap-4 group cursor-pointer" onClick={() => {
              navigator.clipboard.writeText(seg.text); toast.success("Copied");
            }}>
              <span className="font-mono text-xs text-muted-foreground shrink-0 w-12 pt-0.5">
                {formatTime(seg.start_time)}
              </span>
              <div className="min-w-0">
                <div className="font-medium text-xs mb-0.5" style={{ color: speakerColor(seg.speaker) }}>
                  {seg.speaker}
                </div>
                <div className="text-[15px] leading-relaxed">{seg.text}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 h-fit">
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Speakers</div>
        <div className="mt-4 space-y-3">
          {speakers.map((s, i) => (
            <div key={s.name} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ background: SPEAKER_PALETTE[i % SPEAKER_PALETTE.length] }} />
                <span className="font-medium">{s.name}</span>
              </div>
              <span className="font-mono text-xs text-muted-foreground">{s.word_count}w</span>
            </div>
          ))}
        </div>
        <div className="mt-6 font-mono text-xs text-muted-foreground">
          {transcript.word_count} words total
        </div>
      </div>
    </div>
  );
}

function formatTime(sec) {
  const m = Math.floor(sec / 60); const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

const SUGGESTED = [
  "What were the main decisions?",
  "Summarize the action items.",
  "Draft a follow-up email.",
  "What were the pain points discussed?",
];

function ChatTab({ meetingId, hasTranscript, attendees }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get(`/meetings/${meetingId}/chat`).then((r) => setMessages(r.data.messages));
  }, [meetingId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (msg) => {
    if (!msg.trim() || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: msg, message_id: `tmp-${Date.now()}` }]);
    setInput("");
    try {
      const res = await api.post(`/meetings/${meetingId}/chat`, { message: msg });
      setMessages((m) => [
        ...m.filter((x) => x.message_id !== `tmp-${Date.now()}`),
        res.data.user_message, res.data.assistant_message,
      ]);
    } catch (e) {
      toast.error("Chat failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (!hasTranscript) {
    return <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">Chat will be available once the transcript is ready.</div>;
  }

  return (
    <div className="rounded-2xl border border-border bg-card flex flex-col h-[65vh]">
      <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Sparkles className="mx-auto h-6 w-6 text-primary" />
            <h3 className="mt-3 font-display text-xl font-medium">Ask anything about this meeting</h3>
            <p className="mt-2 text-sm text-muted-foreground">Powered by Claude Sonnet 4.5 with the full transcript in context.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {SUGGESTED.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="rounded-full border border-border bg-background px-3 py-1.5 text-xs hover:border-primary hover:text-primary"
                  data-testid={`suggested-${s.slice(0, 10)}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.message_id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
              m.role === "user" ? "bg-primary text-primary-foreground" : "bg-accent"
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-2xl bg-accent px-4 py-2.5 text-sm">
              <span className="font-mono text-xs text-muted-foreground">thinking…</span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="border-t border-border p-4 flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about this meeting…"
          disabled={sending}
          data-testid="chat-input"
        />
        <Button type="submit" size="icon" disabled={sending || !input.trim()} data-testid="chat-send">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}

function InsightsTab({ transcript, summary }) {
  if (!transcript) {
    return <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">Insights load once the transcript is ready.</div>;
  }

  const speakers = transcript.speakers || [];
  const pieData = speakers.map((s, i) => ({
    name: s.name, value: s.total_seconds, color: SPEAKER_PALETTE[i % SPEAKER_PALETTE.length],
  }));
  const totalSec = speakers.reduce((a, s) => a + s.total_seconds, 0) || 1;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Talk time</div>
        <div className="mt-4 h-64">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90}>
                {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(v) => `${Math.round(v)}s`} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 space-y-2">
          {speakers.map((s, i) => {
            const pct = Math.round((s.total_seconds / totalSec) * 100);
            const wpm = s.total_seconds > 0 ? Math.round((s.word_count / s.total_seconds) * 60) : 0;
            return (
              <div key={s.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: SPEAKER_PALETTE[i % SPEAKER_PALETTE.length] }} />
                  <span className="font-medium">{s.name}</span>
                </div>
                <span className="font-mono text-xs text-muted-foreground">{pct}% · {wpm} WPM</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-6">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Sentiment</div>
          <div className="mt-4 flex items-center gap-3">
            <SentimentDot value={summary?.sentiment} />
            <span className="font-display text-3xl font-medium capitalize">{summary?.sentiment || "neutral"}</span>
          </div>
          {summary?.sentiment_details?.speakers?.length > 0 && (
            <div className="mt-6 space-y-2">
              {summary.sentiment_details.speakers.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span>{s.name}</span>
                  <div className="flex items-center gap-2">
                    <SentimentDot value={s.sentiment} />
                    <span className="text-muted-foreground capitalize">{s.sentiment}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {summary?.topics?.length > 0 && (
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Topics</div>
            <ul className="mt-4 space-y-2">
              {summary.topics.map((t, i) => (
                <li key={i} className="flex items-center justify-between text-sm">
                  <span>{t.name}</span>
                  {t.timestamps && <span className="font-mono text-xs text-muted-foreground">{t.timestamps}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function CalendarOverrideBanner({ meeting, onChanged }) {
  const [busy, setBusy] = useState(false);
  const willRecord = ["auto_yes", "user_override_yes"].includes(meeting.auto_record_decision);
  const start = meeting.scheduled_at ? new Date(meeting.scheduled_at) : null;
  const timeStr = start
    ? start.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : "";

  const apply = async (decision, applyToSeries = false) => {
    setBusy(true);
    try {
      await api.post(`/calendar/meetings/${meeting.meeting_id}/override`, {
        decision, apply_to_series: applyToSeries,
      });
      toast.success(decision === "force_skip"
        ? (applyToSeries ? "Series skipped" : "Meeting skipped")
        : (applyToSeries ? "Series will record" : "Will record this meeting"));
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Override failed");
    } finally { setBusy(false); }
  };

  const tone = willRecord
    ? "border-emerald-200 bg-emerald-50 dark:border-emerald-900/40 dark:bg-emerald-950/30"
    : "border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30";

  return (
    <div className={`mb-6 rounded-xl border p-5 ${tone}`} data-testid="calendar-override-banner">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-display text-lg font-medium">
            {willRecord ? `Bot will join at ${timeStr}` : "Bot will not join this meeting"}
          </div>
          <p className="mt-1 text-sm text-foreground/80">
            {meeting.auto_record_reason || (willRecord
              ? "Noteology will record, transcribe, and summarize."
              : "This meeting is currently skipped based on your auto-record rules.")}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {willRecord ? (
              <>
                <Button size="sm" variant="outline" disabled={busy}
                  onClick={() => apply("force_skip", false)}
                  data-testid="banner-skip-once">
                  Skip this meeting
                </Button>
                {meeting.recurring_event_id && (
                  <Button size="sm" variant="ghost" disabled={busy}
                    onClick={() => apply("force_skip", true)}
                    data-testid="banner-skip-series">
                    Skip whole series
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button size="sm" disabled={busy}
                  onClick={() => apply("force_record", false)}
                  data-testid="banner-record-once">
                  Record this meeting
                </Button>
                {meeting.recurring_event_id && (
                  <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => apply("force_record", true)}
                    data-testid="banner-record-series">
                    Record whole series
                  </Button>
                )}
                <Link to="/app/settings?tab=calendar" className="inline-flex items-center text-xs underline">
                  Edit rules
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FailedBanner({ meeting, onRetry, onReload }) {
  const code = meeting.error_code || "Unknown";
  const msg = meeting.error_message || "The bot couldn't join this meeting.";
  const isAutoRetrying = code === "ZoomMeetingNotStarted";
  const action = {
    ZoomMeetingRegistrationRequired: "Re-send the bot with registration details (name + email).",
    ZoomMeetingPasswordWrong: "Re-send the bot with the correct meeting password.",
    ZoomWaitingRoomTimeout: "Ask the host to admit the bot, then retry.",
    ZoomMeetingNotStarted: `Auto-retrying every 2 minutes${meeting.retry_count ? ` (attempt ${meeting.retry_count})` : ""}.`,
    ZoomRecordingDisabled: "Ask the host to enable recording, then retry.",
  }[code] || "Try again, or contact support if this persists.";

  return (
    <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-5 dark:border-red-900/40 dark:bg-red-950/30" data-testid="failed-banner">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-full bg-red-100 p-1.5 dark:bg-red-900/50">
          <AlertCircle className="h-4 w-4 text-red-700 dark:text-red-300" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-lg font-medium text-red-900 dark:text-red-200">Bot couldn't join</h3>
            <span className="font-mono text-[10px] uppercase tracking-wider text-red-700/70 dark:text-red-300/70">{code}</span>
          </div>
          <p className="mt-1 text-sm text-red-800 dark:text-red-200">{msg}</p>
          <p className="mt-2 text-xs text-red-700/80 dark:text-red-300/80">{action}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {!isAutoRetrying && meeting.meeting_url && (
              <Button size="sm" variant="default" onClick={onRetry} data-testid="retry-bot-button">
                <RefreshCw className="mr-2 h-3 w-3" /> Retry with new details
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={onReload} data-testid="reload-meeting-button">
              Refresh status
            </Button>
            <a
              href="mailto:support@noteology.ai"
              className="text-xs font-mono uppercase tracking-wider text-red-700/80 hover:text-red-700 dark:text-red-300/80 dark:hover:text-red-200"
              data-testid="contact-support-link"
            >
              Contact support <ExternalLink className="inline h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function buildMarkdown(meeting, summary, actions) {
  let md = `# ${meeting.title}\n\n`;
  md += `**Date:** ${new Date(meeting.scheduled_at).toLocaleString()}\n`;
  md += `**Duration:** ${Math.round((meeting.duration_seconds || 0) / 60)} min\n\n`;
  if (summary) {
    md += `## Summary\n\n${summary.summary}\n\n`;
    if (summary.key_decisions?.length) {
      md += `## Key Decisions\n\n`;
      summary.key_decisions.forEach((d) => (md += `- ${d}\n`));
      md += "\n";
    }
  }
  if (actions?.length) {
    md += `## Action Items\n\n`;
    actions.forEach((a) => {
      md += `- [${a.status === "done" ? "x" : " "}] **${a.owner}** — ${a.task}`;
      if (a.due_date) md += ` (due ${a.due_date})`;
      md += "\n";
    });
  }
  return md;
}
